"use client";

import { motion } from "framer-motion";
import {
    BookOpen,
    CheckCircle,
    Download,
    Gauge,
    Play,
    Rocket,
    Shield,
    Share2,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import PageTemplate from "@/components/PageTemplate";
import { Button } from "@/components/ui/button";
import { Products } from "@/types/productsTypes";
import TemplateInformationOverlay from "@/components/TemplateInformationOverlay";
import FloatingAction from "src/components/FloatingAction";

const buttonIconMap = {
    rocket: Rocket,
    play: Play,
    book: BookOpen,
    gauge: Gauge,
    download: Download,
    shield: Shield,
    share: Share2,
} as const;


export default function ChatIDE({ section }: { section: Products['atlaside']['chatIDE'] }) {
    return (
        <section className="section-primary text-center">
                    <div className="grid lg:grid-cols-2 gap-5 items-center">
                        <motion.div 
                            className="text-left lg:text-left"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ staggerChildren: 0.2, delayChildren: 0.1 }}
                        >
                            <motion.p
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                className="w-fit badge-size section-badge mb-1"
                            >
                                {section.badge}
                            </motion.p>
                            
                            <motion.h1 
                                className="title-size max-w-2xl font-bold mb-3 text-bg"
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                            >
                                {section.title}
                            </motion.h1>
                            
                            <motion.p 
                                className="text-size text-muted-foreground mb-8 max-w-2xl"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                            >
                                {section.description}
                            </motion.p>

                            <motion.ul 
                                className="flex flex-col gap-2 mb-8 list-size"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: { opacity: 1 }
                                }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                            >
                                {section.bullets.map((text , i) => (
                                    <motion.li
                                        key={i}
                                        className="flex items-start gap-2"
                                        variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
                                        transition={{ duration: 0.4, delay: 0.1 * i, ease: "easeOut" }}
                                    >
                                        <CheckCircle className="size-4 min-w-4 mt-1.5 text-primary-600" />
                                        {text}
                                    </motion.li>
                                ))}
                            </motion.ul>
                            
                            <motion.div 
                                className="flex flex-col sm:flex-row gap-4 mb-8"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                            >
                                <Link href={section.primaryButton.link} target={section.primaryButton.target}>
                                    <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {(() => {
                                            const Icon = section.primaryButton.icon
                                                ? buttonIconMap[section.primaryButton.icon as keyof typeof buttonIconMap]
                                                : Rocket;
                                            return (
                                                <Button
                                                    variant={section.primaryButton.variant || "default"}
                                                    size="xl"
                                                    className="w-full md:w-fit"
                                                >
                                                    {Icon && <Icon className="w-5 h-5 mr-2" />}
                                                    {section.primaryButton.text}
                                                </Button>
                                            );
                                        })()}
                                    </motion.div>
                                </Link>
                                <Link href={section.secondaryButton.link} target={section.secondaryButton.target}>
                                    <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {(() => {
                                            const Icon = section.secondaryButton.icon
                                                ? buttonIconMap[section.secondaryButton.icon as keyof typeof buttonIconMap]
                                                : Play;
                                            return (
                                                <Button
                                                    variant={section.secondaryButton.variant || "outline"}
                                                    size="xl"
                                                    className="w-full md:w-fit"
                                                >
                                                    {Icon && <Icon className="w-5 h-5 mr-2" />}
                                                    {section.secondaryButton.text}
                                                </Button>
                                            );
                                        })()}
                                    </motion.div>
                                </Link>
                            </motion.div>
                        </motion.div>

                        {/* hero image with PageTemplate */}
                        <motion.div 
                            className="relative"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                        >
                            <PageTemplate>
                                <div className="relative">
                                    <Image 
                                        src={section.heroImage}
                                        alt="AtlasIDE Interface" 
                                        width={1000} 
                                        height={1000} 
                                        className="w-full h-auto rounded-xl"
                                    />
                                    
                                    {/* Chat AI Information Overlay */}
                                    <TemplateInformationOverlay overlay={section.overlay} />
                                </div>
                                
                                <FloatingAction position="-bottom-4 -right-4" text={section.floatingAction.text} icon={section.floatingAction.icon} />
                            </PageTemplate>
                        </motion.div>
                    </div>
                </section>
    )
}